import React from 'react';

import styled from '@emotion/styled';

import { WarehouseInput } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { CheckboxField } from '@admin/styled/checkbox_field';
import { DetailGrid } from '@admin/styled/detail_grid';
import { DetailItem } from '@admin/styled/detail_item';
import { DetailLabel } from '@admin/styled/detail_label';
import { DetailValue } from '@admin/styled/detail_value';
import { Field } from '@admin/styled/field';
import { FieldCheckbox } from '@admin/styled/field_checkbox';
import { FieldHint } from '@admin/styled/field_hint';
import { FieldInput } from '@admin/styled/field_input';
import { FieldLabel } from '@admin/styled/field_label';
import { FormBody } from '@admin/styled/form_body';
import { FormRow } from '@admin/styled/form_row';
import { MutedText } from '@admin/styled/muted_text';

import { FacilityTextInput } from '../facilities/facility_text_input';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
type Day = (typeof DAYS)[number];

const AccessHoursList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AccessHoursRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr 24px 1fr auto;
  gap: 8px;
  align-items: center;
`;

const DayLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1a1f36;
  text-transform: capitalize;
`;

const ToLabel = styled.span`
  font-size: 13px;
  color: #6b7385;
  text-align: center;
`;

const TimeInput = styled(FieldInput)`
  min-width: 0;
`;

function formatAccessHours(warehouse: WarehouseInput): string {
  const lines = DAYS.map((day) => {
    const hours = warehouse.customerAccessHours?.find((row) => row.day === day);
    if (!hours?.open && !hours?.close) {
      return null;
    }
    const open = hours?.open || '—';
    const close = hours?.close || '—';
    return `${day.charAt(0).toUpperCase()}${day.slice(1)}: ${open} – ${close}`;
  }).filter(Boolean);

  return lines.length > 0 ? lines.join('; ') : '';
}

export const WarehouseOperationsDetail: React.FC<{ warehouse: WarehouseInput }> = ({ warehouse }) => {
  const accessSummary = formatAccessHours(warehouse);

  return (
    <DetailGrid>
      <DetailItem>
        <DetailLabel>Height</DetailLabel>
        <DetailValue>
          {warehouse.height != null && !Number.isNaN(warehouse.height) ? (
            warehouse.height
          ) : (
            <MutedText>Not set</MutedText>
          )}
        </DetailValue>
      </DetailItem>
      <DetailItem>
        <DetailLabel>Storage Treasures facility ID</DetailLabel>
        <DetailValue>
          {warehouse.storageTreasuresFacilityID ? warehouse.storageTreasuresFacilityID : <MutedText>Not set</MutedText>}
        </DetailValue>
      </DetailItem>
      <DetailItem>
        <DetailLabel>Third party logistics</DetailLabel>
        <DetailValue>{warehouse.thirdPartyLogistics ? 'Yes' : 'No'}</DetailValue>
      </DetailItem>
      <DetailItem style={{ gridColumn: '1 / -1' }}>
        <DetailLabel>Customer access instructions</DetailLabel>
        <DetailValue>
          {warehouse.customerAccessInstructions ? warehouse.customerAccessInstructions : <MutedText>Not set</MutedText>}
        </DetailValue>
      </DetailItem>
      <DetailItem style={{ gridColumn: '1 / -1' }}>
        <DetailLabel>Customer access hours</DetailLabel>
        <DetailValue>{accessSummary ? accessSummary : <MutedText>Not set</MutedText>}</DetailValue>
      </DetailItem>
    </DetailGrid>
  );
};

const AccessHoursInput: React.FC<{
  dayToUpdate: Day;
  editAccess: boolean;
  warehouse: WarehouseInput;
  onChange(warehouse: WarehouseInput): void;
}> = ({ dayToUpdate, editAccess, warehouse, onChange }) => {
  function updateCustomerAccessHours(newTime: string, typesToUpdate: Array<'open' | 'close'>) {
    const updatedHours = warehouse.customerAccessHours.map((oldAccessHours) => {
      if (oldAccessHours.day === dayToUpdate) {
        return {
          day: dayToUpdate,
          open: typesToUpdate.includes('open') ? newTime : oldAccessHours.open,
          close: typesToUpdate.includes('close') ? newTime : oldAccessHours.close,
        };
      }
      return oldAccessHours;
    });

    onChange({
      ...warehouse,
      customerAccessHours: updatedHours,
    });
  }

  const relevantAccessHours = warehouse.customerAccessHours?.find((hours) => hours.day === dayToUpdate);

  return (
    <AccessHoursRow>
      <DayLabel>{dayToUpdate}</DayLabel>
      <TimeInput
        id={`warehouse__customer_access_hours_${dayToUpdate}__open`}
        type="time"
        value={relevantAccessHours?.open ?? ''}
        onChange={(event) => updateCustomerAccessHours(event.target.value, ['open'])}
        disabled={!editAccess}
      />
      <ToLabel>to</ToLabel>
      <TimeInput
        id={`warehouse__customer_access_hours_${dayToUpdate}__close`}
        type="time"
        value={relevantAccessHours?.close ?? ''}
        onChange={(event) => updateCustomerAccessHours(event.target.value, ['close'])}
        disabled={!editAccess}
      />
      {editAccess && (
        <Button
          variant="danger"
          size="sm"
          type="button"
          onClick={() => updateCustomerAccessHours('', ['open', 'close'])}
        >
          Clear
        </Button>
      )}
    </AccessHoursRow>
  );
};

export const WarehouseOperationsForm: React.FC<{
  warehouse: WarehouseInput;
  editAccess: boolean;
  onChange(warehouse: WarehouseInput): void;
}> = ({ warehouse, editAccess, onChange }) => (
  <FormBody>
    <FormRow>
      <Field>
        <FieldLabel htmlFor="warehouse__height">Height</FieldLabel>
        <FacilityTextInput
          id="warehouse__height"
          type="number"
          value={warehouse.height ?? ''}
          onChange={(event) => {
            const parsed = parseInt(event.target.value, 10);
            onChange({ ...warehouse, height: Number.isNaN(parsed) ? undefined : parsed });
          }}
          disabled={!editAccess}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="warehouse__storage_treasures_facility_id">Storage Treasures facility ID</FieldLabel>
        <FacilityTextInput
          id="warehouse__storage_treasures_facility_id"
          type="text"
          value={warehouse.storageTreasuresFacilityID ?? ''}
          onChange={(event) => onChange({ ...warehouse, storageTreasuresFacilityID: event.target.value })}
          disabled={!editAccess}
        />
      </Field>
    </FormRow>

    <Field>
      <FieldLabel htmlFor="warehouse__customer_access_instructions">Customer access instructions</FieldLabel>
      <FacilityTextInput
        id="warehouse__customer_access_instructions"
        type="text"
        value={warehouse.customerAccessInstructions ?? ''}
        onChange={(event) =>
          onChange({
            ...warehouse,
            customerAccessInstructions: event.target.value !== '' ? event.target.value : null,
          })
        }
        disabled={!editAccess}
      />
    </Field>

    <Field>
      <FieldLabel>Customer access hours</FieldLabel>
      <FieldHint>Leave times empty for days the site is closed to customers.</FieldHint>
      <AccessHoursList>
        {DAYS.map((day) => (
          <AccessHoursInput
            key={day}
            dayToUpdate={day}
            editAccess={editAccess}
            warehouse={warehouse}
            onChange={onChange}
          />
        ))}
      </AccessHoursList>
    </Field>

    <CheckboxField>
      <FieldCheckbox
        id="warehouse__third_party_logistics"
        type="checkbox"
        checked={warehouse.thirdPartyLogistics}
        onChange={(event) => onChange({ ...warehouse, thirdPartyLogistics: event.target.checked })}
        disabled={!editAccess}
      />
      Third party logistics
    </CheckboxField>
  </FormBody>
);
